import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Checkbox, Spin } from 'antd';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import styles from './AssignmentActionFlowModal.module.scss';

const SCOPE_LABELS: Record<string, string> = {
	roles: 'Roles',
	groups: 'Groups',
	group_labels: 'Organization',
	users: 'Users'
};

interface Props {
	assignmentId: number;
	scopes: string[];
	selectedUserIds: string[];
	onToggle: (userId: string) => void;
}

const AssignmentUsersPicker: React.FC<Props> = observer(
	({ assignmentId, scopes, selectedUserIds, onToggle }) => {
		const { learningManagerStore, assignUsersStore } = useStores();
		const { assignmentUsers } = assignUsersStore;
		const [scopeNames, setScopeNames] = useState<string[]>([]);
		const scopeKey = scopes.join(',');

		useEffect(() => {
			if (!assignmentId) return;
			let cancelled = false;
			(async () => {
				try {
					const userIds = await learningManagerStore.getAssignmentSourceUsers(
						assignmentId
					);
					if (!cancelled) {
						await assignUsersStore.fetchUsersByIds(userIds);
					}
				} catch {
					// failures are reflected via the status flags below
				}
			})();
			return () => {
				cancelled = true;
			};
		}, [assignmentId]);

		// Resolve the assignment's scopes into real entity names for the badges.
		// (Users are already listed below, so they aren't badged.)
		useEffect(() => {
			if (!assignmentId) return;
			let cancelled = false;
			(async () => {
				const names: string[] = [];
				try {
					if (scopes.includes('roles')) {
						const roles = await learningManagerStore.getAssignmentSourceScopeRoles(
							assignmentId
						);
						names.push(...roles.map((r) => r.name));
					}
					if (scopes.includes('groups')) {
						const groups = await learningManagerStore.getAssignmentSourceScopeGroups(
							assignmentId
						);
						names.push(...groups.map((g) => g.name));
					}
					if (scopes.includes('group_labels')) {
						const labels = await learningManagerStore.getAssignmentSourceScopeGroupLabels(
							assignmentId
						);
						names.push(...labels.map((l) => l.name));
					}
				} catch {
					// fall back to scope-type labels below
				}
				if (!cancelled) setScopeNames(names);
			})();
			return () => {
				cancelled = true;
			};
		}, [assignmentId, scopeKey]);

		// Real names when available; otherwise the scope-type labels.
		const badges =
			scopeNames.length > 0
				? scopeNames
				: scopes
						.filter((scope) => scope !== 'users')
						.map((scope) => SCOPE_LABELS[scope] ?? scope);

		const isLoading =
			learningManagerStore.status.getAssignmentSourceUsers ===
				Status.Pending ||
			assignUsersStore.status.fetchUsersByIds === Status.Pending;

		return (
			<div id="lms-ca-assignment-users-picker">
				{badges.length > 0 && (
					<div className={styles.scopeBadges}>
						{badges.map((label, index) => (
							<span
								key={`${label}-${index}`}
								className={styles.scopeBadge}
							>
								{label}
							</span>
						))}
					</div>
				)}

				<div className={styles.usersHeader}>
					Users ({assignmentUsers.length})
				</div>

				{isLoading ? (
					<div className={styles.pickerLoading}>
						<Spin />
					</div>
				) : assignmentUsers.length === 0 ? (
					<div className={styles.pickerEmpty}>
						No users associated with this assignment.
					</div>
				) : (
					<div className={styles.userList}>
						{assignmentUsers.map((user) => (
							<div
								key={user.id}
								className={styles.userRow}
								onClick={() => onToggle(user.id)}
							>
								<Checkbox
									checked={selectedUserIds.includes(user.id)}
									onChange={() => undefined}
									style={{ pointerEvents: 'none' }}
								/>
								<div className={styles.userInfo}>
									<div className={styles.userName}>
										{user.firstName} {user.lastName}
									</div>
									<div className={styles.userEmail}>
										{user.email}
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		);
	}
);

export default AssignmentUsersPicker;
